import React, { useState, useEffect, useRef, useMemo } from "react";
import { useUser } from "../../shared/context/UserContext";
import { listProfiles, saveProfile, deleteProfile } from "./dataExchangeApi";
import { useDataExchangeManifest } from "./useDataExchangeManifest";

function sampleSnippet(colIdx, sampleRows) {
  const vals = sampleRows
    .map(row => row[colIdx] || "")
    .filter(v => v && v !== "None")
    .slice(0, 3);
  return vals.join(", ") || "\u2014";
}

export default function ColumnMapper({ parseResult, mapping, setMapping, suggestions = {}, siteName, onNext, onBack, loading }) {
  const { callerEmail } = useUser();
  const { column_headers, sample_rows, row_count } = parseResult;

  const { fieldGroups, loading: manifestLoading, error: manifestError } = useDataExchangeManifest();

  const [profiles, setProfiles] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [showLoad, setShowLoad] = useState(false);
  const loadRef = useRef(null);

  const requiredKeyFields = useMemo(() => {
    const keyGroup = fieldGroups.find(g => g.label === "Key Fields");
    return keyGroup ? keyGroup.fields.filter(f => f.required).map(f => f.key) : [];
  }, [fieldGroups]);

  useEffect(() => {
    if (callerEmail) {
      listProfiles("import", callerEmail).then(setProfiles).catch(() => {});
    }
  }, [callerEmail]);

  useEffect(() => {
    function handleClick(e) {
      if (loadRef.current && !loadRef.current.contains(e.target)) setShowLoad(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  async function handleSaveProfile() {
    if (!saveName.trim()) return;
    await saveProfile("import", saveName.trim(), mapping, callerEmail);
    setShowSave(false);
    setSaveName("");
    listProfiles("import", callerEmail).then(setProfiles).catch(() => {});
  }

  function handleLoadProfile(profile) {
    setMapping(profile.profile);
    setShowLoad(false);
  }

  async function handleDeleteProfile(e, profileId) {
    e.stopPropagation();
    await deleteProfile(profileId, callerEmail);
    setProfiles(prev => prev.filter(p => p.id !== profileId));
  }

  // Effective mapping — suggestions fill in where the user hasn't interacted
  const effectiveMapping = useMemo(() => ({ ...suggestions, ...mapping }), [suggestions, mapping]);

  const mappedCount = Object.values(effectiveMapping).filter(v => v).length;
  const keysMapped = requiredKeyFields.every(kf => Object.values(effectiveMapping).includes(kf));

  const requiredCols = [];
  const additionalCols = [];
  for (const header of column_headers) {
    const val = effectiveMapping[header];
    if (val && requiredKeyFields.includes(val)) {
      requiredCols.push(header);
    } else {
      additionalCols.push(header);
    }
  }

  function handleChange(header, value) {
    setMapping(prev => ({ ...prev, [header]: value || null }));
  }

  function renderRow(header) {
    const colIdx = column_headers.indexOf(header);
    const effectiveVal = effectiveMapping[header] || "";
    const isSuggested = mapping[header] === undefined && !!suggestions[header];
    const isKeyMapped = effectiveVal && requiredKeyFields.includes(effectiveVal);
    const isMissingKey = requiredCols.includes(header) && !effectiveVal;

    return (
      <div key={header} className={`cm-row ${isKeyMapped ? "cm-row-key" : ""} ${isMissingKey ? "cm-row-error" : ""}`}>
        <div className="cm-col-name">{header}</div>
        <div className="cm-col-sample">{sampleSnippet(colIdx, sample_rows)}</div>
        <div className="cm-col-mapping">
          <select
            className="de-select"
            value={effectiveVal}
            onChange={e => handleChange(header, e.target.value)}
          >
            <option value="">Skip</option>
            {fieldGroups.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.fields.map(f => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {isSuggested && (
            <span
              className="cm-suggested-badge"
              style={{
                marginLeft: "var(--space-2)",
                fontSize: "var(--font-size-xs)",
                fontStyle: "italic",
                color: "var(--text-muted)",
              }}
            >
              Suggested
            </span>
          )}
        </div>
      </div>
    );
  }

  if (manifestLoading) {
    return <div className="de-panel">Loading field options...</div>;
  }

  if (manifestError) {
    return (
      <div className="de-panel">
        <div className="de-banner de-banner-error">
          Could not load field manifest: {manifestError}
        </div>
      </div>
    );
  }

  return (
    <div className="de-panel">
      <div className="cm-info">
        <span>{row_count} rows</span>
        <span>Site: {siteName}</span>
      </div>

      {/* Profile bar */}
      <div className="de-profile-bar">
        <div className="de-profile-actions">
          <div className="de-profile-load-wrap" ref={loadRef}>
            <button className="de-profile-btn" onClick={() => setShowLoad(!showLoad)}>
              Load Profile &#9662;
            </button>
            {showLoad && (
              <div className="de-profile-dropdown">
                {profiles.length === 0 ? (
                  <div className="de-profile-dropdown-empty">No saved profiles</div>
                ) : (
                  profiles.map(p => (
                    <div key={p.id} className="de-profile-dropdown-item" onClick={() => handleLoadProfile(p)}>
                      <span className="de-profile-dropdown-name">{p.name}</span>
                      <button className="de-profile-dropdown-delete" onClick={e => handleDeleteProfile(e, p.id)}>
                        Delete
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          {showSave ? (
            <div className="de-profile-save-inline">
              <input
                type="text"
                className="de-profile-save-input"
                placeholder="Profile name..."
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSaveProfile()}
                autoFocus
              />
              <button className="de-profile-btn" onClick={handleSaveProfile} disabled={!saveName.trim()}>Save</button>
              <button className="de-profile-btn" onClick={() => { setShowSave(false); setSaveName(""); }}>Cancel</button>
            </div>
          ) : (
            <button className="de-profile-btn" onClick={() => setShowSave(true)}>Save Profile</button>
          )}
        </div>
      </div>

      {/* Required section */}
      {requiredCols.length > 0 && (
        <div className="cm-section">
          <div className="cm-section-header cm-section-required">REQUIRED — must be mapped</div>
          <div className="cm-header-row">
            <div className="cm-col-name">Source Column</div>
            <div className="cm-col-sample">Sample Values</div>
            <div className="cm-col-mapping">TMG Field</div>
          </div>
          {requiredCols.map(renderRow)}
        </div>
      )}

      {/* Additional section */}
      <div className="cm-section">
        <div className="cm-section-header">Additional Columns</div>
        {requiredCols.length === 0 && (
          <div className="cm-header-row">
            <div className="cm-col-name">Source Column</div>
            <div className="cm-col-sample">Sample Values</div>
            <div className="cm-col-mapping">TMG Field</div>
          </div>
        )}
        {additionalCols.map(renderRow)}
      </div>

      {!keysMapped && (
        <div className="de-banner de-banner-error" style={{ marginTop: "var(--space-2)" }}>
          Required key fields must be mapped: Tool ID and Tool Type
        </div>
      )}

      <div className="iw-footer">
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <button className="btn btn-secondary" onClick={onBack}>&larr; Back</button>
          <span className="de-field-count">{mappedCount} of {column_headers.length} columns mapped</span>
        </div>
        <button
          className="btn btn-primary"
          disabled={!keysMapped || loading}
          onClick={onNext}
        >
          {loading ? "Validating..." : "Validate Mapping \u2192"}
        </button>
      </div>
    </div>
  );
}
